import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useApi, getErrorMessage } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, CheckCircle2, Wrench, ChevronLeft,
  Building2, Star, Send, Plus, X, Mail, MessageSquare, RefreshCw,
  ThumbsUp, Wrench as WrenchIcon, AlertOctagon, Repeat, Calendar,
  ImageIcon, Info
} from 'lucide-react';

// ---------- helpers ----------
function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) {
    const hours = Math.floor((Date.now() - d.getTime()) / 3600000);
    if (hours < 1) return "à l'instant";
    if (hours === 1) return 'il y a 1 heure';
    return `il y a ${hours} heures`;
  }
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem`;
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
  return d.toLocaleDateString('fr-FR');
}

function exactDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
}

const SOURCE_LABELS = {
  conversation: { label: 'Conversation Beds24', cls: 'bg-blue-50 text-blue-600' },
  comment: { label: 'Avis public', cls: 'bg-purple-50 text-purple-600' },
  private_remark: { label: 'Avis privé', cls: 'bg-orange-50 text-orange-600' },
  contractor_note: { label: 'Remarque artisan', cls: 'bg-teal-50 text-teal-600' },
};

const STATUS_LABELS = {
  nouveau: { label: 'Nouveau', cls: 'bg-[#FFF0F1] text-[#C73E51]', dot: '#FF5A6E' },
  en_cours: { label: 'En cours', cls: 'bg-amber-50 text-amber-700', dot: '#F59E0B' },
  resolu: { label: 'Résolu', cls: 'bg-emerald-50 text-emerald-700', dot: '#10B981' },
};

// ---------- Timeline dot ----------
const TLDot = ({ tone = 'muted', children }) => {
  const tones = {
    danger: 'border-[#FF5A6E] bg-[#FFF0F1] text-[#C73E51]',
    warn: 'border-amber-400 bg-amber-50 text-amber-700',
    success: 'border-emerald-400 bg-emerald-50 text-emerald-700',
    info: 'border-blue-400 bg-blue-50 text-blue-700',
    muted: 'border-slate-300 bg-white text-slate-500',
  };
  return (
    <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white ${tones[tone] || tones.muted}`}>
      {children}
    </div>
  );
};

const TLItem = ({ tone, icon, children, isLast }) => (
  <div className="relative pl-9 pb-6">
    {!isLast && <div className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-slate-200" />}
    <TLDot tone={tone}>{icon}</TLDot>
    <div>{children}</div>
  </div>
);

export const IssueDetailPage = () => {
  const { commentId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [comment, setComment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [missionNote, setMissionNote] = useState('');
  const [deadlinePreset, setDeadlinePreset] = useState('48h');
  const [customDeadline, setCustomDeadline] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [mission, setMission] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const fetchComment = useCallback(async () => {
    try {
      const res = await api.get(`/guest-comments/${commentId}`);
      setComment(res.data);
    } catch {
      toast.error('Commentaire non trouvé');
      navigate('/issues');
    } finally {
      setLoading(false);
    }
  }, [api, commentId, navigate]);

  useEffect(() => { fetchComment(); }, [fetchComment]);

  useEffect(() => {
    if (comment?.mission_id) {
      api.get(`/missions/${comment.mission_id}`).then(r => setMission(r.data)).catch(() => {});
    }
  }, [api, comment?.mission_id]);

  const openAssignModal = async () => {
    try {
      const res = await api.get('/contractors');
      setContractors(res.data);
      setSelectedContractors([]);
      setMissionNote('');
      setDeadlinePreset('48h');
      setCustomDeadline('');
      setShowAssign(true);
    } catch {
      toast.error('Erreur chargement des contacts');
    }
  };

  const toggleContractor = (id) => {
    setSelectedContractors(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (selectedContractors.length === 0) {
      toast.error('Sélectionnez au moins un contact');
      return;
    }
    if (deadlinePreset === 'custom' && !customDeadline) {
      toast.error('Choisissez une date limite');
      return;
    }
    setAssigning(true);
    try {
      const payload = {
        issue_id: commentId,
        contractor_ids: selectedContractors,
        note: missionNote,
        deadline_preset: deadlinePreset,
      };
      if (deadlinePreset === 'custom') {
        payload.custom_deadline_at = new Date(customDeadline).toISOString();
      }
      const res = await api.post('/missions/assign', payload);
      toast.success(`Mission envoyée à ${res.data.emails_sent} contact(s)`);
      setShowAssign(false);
      fetchComment();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'envoi de la mission"));
    } finally {
      setAssigning(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await api.patch(`/guest-comments/${commentId}/status`, {
        status: newStatus,
        note: note.trim() || null
      });
      setComment(res.data);
      setNote('');
      toast.success(`Statut mis à jour : ${STATUS_LABELS[newStatus]?.label}`);
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  // Build a single chronological timeline from all sources
  const timeline = useMemo(() => {
    if (!comment) return [];
    const events = [];

    // Original signal (the comment itself)
    events.push({
      type: 'signal',
      ts: comment.incident_date || comment.comment_date || comment.created_at,
      data: {
        guest_name: comment.guest_name,
        verbatim: comment.comment_text,
        source: comment.source,
        rating: comment.rating,
      },
    });

    // Recurrences (skip first one if it duplicates the signal)
    (comment.reported_by || []).slice(1).forEach((r, i) => {
      events.push({
        type: 'recurrence',
        ts: r.date,
        data: { ...r, index: i + 2 },
      });
    });

    // Internal notes
    (comment.internal_notes || []).forEach(n => {
      events.push({
        type: 'note',
        ts: n.created_at,
        data: n,
      });
    });

    // Mission events
    if (mission) {
      events.push({
        type: 'mission_sent',
        ts: mission.created_at || mission.sent_at,
        data: mission,
      });
      if (mission.accepted_at) {
        events.push({
          type: 'mission_accepted',
          ts: mission.accepted_at,
          data: mission,
        });
      }
      if (mission.status === 'terminee' && (mission.completion_note || mission.completion_images?.length)) {
        events.push({
          type: 'mission_completed',
          ts: mission.completed_at || mission.updated_at,
          data: mission,
        });
      }
    }

    // Sort chronologically
    events.sort((a, b) => new Date(a.ts || 0) - new Date(b.ts || 0));
    return events;
  }, [comment, mission]);

  if (loading || !comment) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  const status = STATUS_LABELS[comment.tracking_status] || STATUS_LABELS.nouveau;
  const source = SOURCE_LABELS[comment.source] || { label: comment.source, cls: 'bg-slate-50 text-slate-600' };
  const sevColor = comment.issue_severity === 'probleme_majeur' ? '#FF5A6E'
    : comment.issue_severity === 'probleme_mineur' ? '#F59E0B' : '#10B981';
  const sevLabel = comment.issue_severity === 'probleme_majeur' ? 'Problème majeur'
    : comment.issue_severity === 'probleme_mineur' ? 'Problème mineur' : 'Pas de problème';

  return (
    <MainLayout>
      {/* Breadcrumb topbar */}
      <header className="bg-white border-b border-slate-100 px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate('/issues')} className="text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="back-btn">
              <ChevronLeft size={16} />
              Alertes
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-slate-500 font-medium truncate max-w-[220px]">{comment.property_name || 'Logement inconnu'}</span>
            <span className="text-slate-300">/</span>
            <span className="text-slate-600 truncate max-w-md">{comment.issue_summary || 'Détail'}</span>
          </div>
          <div className="text-xs text-slate-400 font-mono">#{commentId?.slice(0, 8)}</div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-8 grid grid-cols-3 gap-8" data-testid="issue-detail-page">

        {/* ============ Main column ============ */}
        <div className="col-span-3 lg:col-span-2 space-y-6">

          {/* Header light */}
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="w-2 h-2 rounded-full" style={{ background: sevColor }} title={sevLabel} />
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: sevColor === '#FF5A6E' ? '#C73E51' : sevColor === '#F59E0B' ? '#B45309' : '#047857' }}>
                {sevLabel}
              </span>
              <span className="text-slate-300">·</span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}>
                {comment.tracking_status === 'nouveau' && (
                  <span className="relative flex">
                    <span className="absolute inline-flex h-1.5 w-1.5 rounded-full bg-[#FF5A6E] opacity-50 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF5A6E]" />
                  </span>
                )}
                {status.label}
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-500" title={exactDateTime(comment.incident_date || comment.comment_date || comment.created_at)}>
                Signalé {relativeDate(comment.incident_date || comment.comment_date || comment.created_at)}
              </span>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2 leading-tight">
              {comment.issue_summary || 'Problème détecté'}
            </h1>

            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
              <Building2 size={14} />
              <span className="font-medium text-slate-700">{comment.property_name}</span>
              <span className="text-slate-300">·</span>
              <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${source.cls}`}>{source.label}</span>
              {comment.issue_categories?.length > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="capitalize">{comment.issue_categories.join(', ')}</span>
                </>
              )}
              {comment.recurrence_count > 1 && (
                <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#FFF0F1] text-[#C73E51]">
                  {comment.recurrence_count} signalements
                </span>
              )}
            </div>
          </div>

          {/* Quick actions bar */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] p-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400 px-2">Actions :</span>
            {comment.tracking_status !== 'en_cours' && (
              <button
                onClick={() => updateStatus('en_cours')}
                disabled={updating}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="status-in-progress-btn"
              >
                <Wrench size={13} />
                Passer en cours
              </button>
            )}
            {comment.tracking_status !== 'resolu' && (
              <button
                onClick={() => updateStatus('resolu')}
                disabled={updating}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="status-resolved-btn"
              >
                <CheckCircle2 size={13} />
                Marquer résolu
              </button>
            )}
            {comment.tracking_status === 'resolu' && (
              <button
                onClick={() => updateStatus('nouveau')}
                disabled={updating}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="status-reopen-btn"
              >
                <AlertTriangle size={13} />
                Rouvrir
              </button>
            )}
            {!mission && comment.issue_severity !== 'aucun_probleme' && (
              <button
                onClick={openAssignModal}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#FF5A6E] hover:bg-[#E84B5F] text-white flex items-center gap-1.5"
              >
                <Send size={13} />
                Assigner à un artisan
              </button>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] p-6">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-5">Historique</h2>

            <div>
              {timeline.map((ev, i) => {
                const isLast = i === timeline.length - 1 && !comment.tracking_status !== 'resolu';
                const channelColor = comment.channel === 'airbnb' ? 'bg-[#FF5A5F]' : 'bg-[#0071C2]';

                // SIGNAL
                if (ev.type === 'signal') {
                  return (
                    <TLItem key={i} tone="danger" icon={<AlertOctagon size={12} />} isLast={false}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5 flex-wrap">
                        <div className={`w-5 h-5 rounded-full ${channelColor} flex items-center justify-center text-white text-[9px] font-bold`}>
                          {comment.channel === 'airbnb' ? 'A' : 'B'}
                        </div>
                        <span className="font-semibold text-slate-700">Signalé par {ev.data.guest_name}</span>
                        <span>·</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_LABELS[ev.data.source]?.cls || 'bg-slate-50 text-slate-600'}`}>
                          {SOURCE_LABELS[ev.data.source]?.label || ev.data.source}
                        </span>
                        {ev.data.rating && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-0.5"><Star size={10} className="text-amber-400" /> {ev.data.rating}/10</span>
                          </>
                        )}
                        <span>·</span>
                        <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 leading-relaxed italic border-l-4 border-slate-300">
                        « {ev.data.verbatim} »
                      </div>
                    </TLItem>
                  );
                }

                // RECURRENCE
                if (ev.type === 'recurrence') {
                  return (
                    <TLItem key={i} tone="warn" icon={<span className="text-[9px] font-bold">{ev.data.index}×</span>} isLast={false}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5">
                        <Repeat size={12} className="text-amber-600" />
                        <span className="font-semibold text-slate-700">Re-signalé par {ev.data.guest_name}</span>
                        {ev.data.date && (
                          <>
                            <span>·</span>
                            <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                          </>
                        )}
                      </div>
                      {ev.data.verbatim && (
                        <p className="text-sm text-slate-600 italic">« {ev.data.verbatim} »</p>
                      )}
                    </TLItem>
                  );
                }

                // INTERNAL NOTE
                if (ev.type === 'note') {
                  const noteStatus = STATUS_LABELS[ev.data.status];
                  return (
                    <TLItem key={i} tone="info" icon={<RefreshCw size={11} />} isLast={false}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1 flex-wrap">
                        <span className="font-semibold text-slate-700">{ev.data.author || 'Vous'}</span>
                        {noteStatus && (
                          <>
                            <span>·</span>
                            <span>Statut passé à</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${noteStatus.cls}`}>
                              {noteStatus.label}
                            </span>
                          </>
                        )}
                        <span>·</span>
                        <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                      </div>
                      {ev.data.text && (
                        <p className="text-sm text-slate-600 italic">« {ev.data.text} »</p>
                      )}
                    </TLItem>
                  );
                }

                // MISSION SENT
                if (ev.type === 'mission_sent') {
                  const recipients = ev.data.sent_to?.map(c => c.company_name).join(' · ') || '?';
                  return (
                    <TLItem key={i} tone="danger" icon={<Send size={11} />} isLast={false}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-semibold text-slate-700">Mission envoyée</span>
                        <span>·</span>
                        <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                      </div>
                      <div className="bg-[#FFF0F1] border border-[#FFD9DE] rounded-lg p-3 mt-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{recipients}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{ev.data.sent_to?.length || 0} prestataire(s) contactés</p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 flex-shrink-0">
                            {ev.data.status === 'envoyee' ? 'En attente' : ev.data.status}
                          </span>
                        </div>
                        {ev.data.sender_note && (
                          <p className="text-xs text-slate-600 mt-2 italic">« {ev.data.sender_note} »</p>
                        )}
                      </div>
                    </TLItem>
                  );
                }

                // MISSION ACCEPTED
                if (ev.type === 'mission_accepted') {
                  return (
                    <TLItem key={i} tone="info" icon={<CheckCircle2 size={11} />} isLast={false}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-semibold text-slate-700">Acceptée par {ev.data.accepted_by?.company_name || '?'}</span>
                        <span>·</span>
                        <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                      </div>
                      {ev.data.scheduled_at && (
                        <p className="text-sm text-slate-600">
                          Créneau prévu : <span className="font-medium text-slate-900">{exactDateTime(ev.data.scheduled_at)}</span>
                        </p>
                      )}
                      {ev.data.acceptance_note && (
                        <p className="text-sm text-slate-500 mt-1 italic">Note artisan : « {ev.data.acceptance_note} »</p>
                      )}
                    </TLItem>
                  );
                }

                // MISSION COMPLETED (avec photos)
                if (ev.type === 'mission_completed') {
                  const photos = ev.data.completion_images || [];
                  return (
                    <TLItem key={i} tone="success" icon={<CheckCircle2 size={12} />} isLast={i === timeline.length - 1}>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5">
                        <span className="font-semibold text-emerald-700">Intervention terminée par {ev.data.accepted_by?.company_name || '?'}</span>
                        <span>·</span>
                        <span title={exactDateTime(ev.ts)}>{relativeDate(ev.ts)}</span>
                      </div>

                      {ev.data.completion_note && (
                        <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded-r-lg p-3 mb-3">
                          <p className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Compte-rendu de l'artisan</p>
                          <p className="text-sm text-slate-700 italic">« {ev.data.completion_note} »</p>
                        </div>
                      )}

                      {photos.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                            <ImageIcon size={12} />
                            Photos d'intervention ({photos.length})
                          </p>
                          <div className="grid grid-cols-4 gap-2">
                            {photos.map((src, idx) => (
                              <button
                                key={idx}
                                onClick={() => setLightbox(src)}
                                className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#FF5A6E] transition-all hover:scale-[1.03]"
                              >
                                <img src={src} alt={`Intervention ${idx + 1}`} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {comment.tracking_status !== 'resolu' && (
                        <div className="mt-3 flex items-center justify-between bg-white border border-emerald-200 rounded-lg p-2.5">
                          <span className="text-xs text-slate-600">
                            <span className="font-medium text-slate-900">Tout est OK ?</span> Clôturez le problème.
                          </span>
                          <button
                            onClick={() => updateStatus('resolu')}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white flex items-center gap-1.5"
                          >
                            <CheckCircle2 size={12} />
                            Clôturer
                          </button>
                        </div>
                      )}
                    </TLItem>
                  );
                }

                return null;
              })}

              {/* Add note input (toujours dernier) */}
              <div className="relative pl-9">
                <TLDot tone="muted"><Plus size={11} /></TLDot>
                <div>
                  <Textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Ajouter une note interne (ex: plombier appelé, problème résolu...)"
                    className="text-sm"
                    rows={2}
                    data-testid="note-input"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-slate-400">La note sera enregistrée avec le prochain changement de statut</span>
                    <span className="text-xs text-slate-400">{note.length} caractères</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Points positifs */}
          {comment.positive_points?.length > 0 && (
            <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <ThumbsUp size={16} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-emerald-800">Points positifs mentionnés</h3>
              </div>
              <ul className="text-sm text-emerald-700 space-y-1.5 pl-6">
                {comment.positive_points.map((p, i) => (
                  <li key={i} className="list-disc">{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ============ Sidebar sticky ============ */}
        <aside className="col-span-3 lg:col-span-1 space-y-4 lg:sticky lg:top-6 lg:self-start">
          {/* Property */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#FFF0F1] flex items-center justify-center">
                <Building2 size={18} className="text-[#FF5A6E]" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 text-sm truncate">{comment.property_name}</p>
                <p className="text-xs text-slate-500">{comment.guest_name}</p>
              </div>
            </div>

            <dl className="space-y-2.5 text-xs">
              <div className="flex justify-between">
                <dt className="text-slate-500">Canal</dt>
                <dd className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${comment.channel === 'airbnb' ? 'bg-[#FF5A5F]' : 'bg-[#0071C2]'}`} />
                  <span className="font-medium text-slate-700">{comment.channel === 'airbnb' ? 'Airbnb' : 'Booking.com'}</span>
                </dd>
              </div>
              {comment.rating && (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Note attribuée</dt>
                  <dd className="font-medium text-slate-700 flex items-center gap-1">
                    <Star size={11} className="text-amber-400 fill-amber-400" />
                    {comment.rating}/10
                  </dd>
                </div>
              )}
              {(comment.incident_date || comment.comment_date) && (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Date du séjour</dt>
                  <dd className="font-medium text-slate-700" title={exactDateTime(comment.incident_date || comment.comment_date)}>
                    {relativeDate(comment.incident_date || comment.comment_date)}
                  </dd>
                </div>
              )}
              <div className="border-t border-slate-100 pt-2.5 flex justify-between">
                <dt className="text-slate-400">Importé le</dt>
                <dd className="text-slate-500">{new Date(comment.created_at).toLocaleDateString('fr-FR')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-400">Analyse IA</dt>
                <dd className={comment.ai_analyzed ? 'text-emerald-500 flex items-center gap-1' : 'text-slate-400'}>
                  {comment.ai_analyzed ? (<><CheckCircle2 size={11} /> Complète</>) : 'Non analysé'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Mission compacte */}
          {mission && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <WrenchIcon size={14} />
                  Intervention
                </h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${
                  mission.status === 'terminee' ? 'bg-emerald-100 text-emerald-700' :
                  mission.status === 'acceptee' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {mission.status === 'terminee' && <CheckCircle2 size={10} />}
                  {mission.status === 'terminee' ? 'Terminée' : mission.status === 'acceptee' ? 'En cours' : 'En attente'}
                </span>
              </div>

              <div className="space-y-3 text-xs">
                {mission.accepted_by && (
                  <div>
                    <p className="text-slate-500 mb-1">{mission.status === 'terminee' ? 'Réalisée par' : 'Acceptée par'}</p>
                    <p className="font-semibold text-slate-900">{mission.accepted_by.company_name}</p>
                    {mission.accepted_by.email && <p className="text-slate-400">{mission.accepted_by.email}</p>}
                  </div>
                )}

                {mission.scheduled_at && mission.status !== 'terminee' && (
                  <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-300">
                    <p className="text-[10px] uppercase font-bold text-blue-700 mb-1">Créneau prévu</p>
                    <p className="font-semibold text-slate-900">{exactDateTime(mission.scheduled_at)}</p>
                  </div>
                )}

                {mission.status === 'terminee' && mission.completion_note && (
                  <div className="bg-emerald-50 rounded-lg p-3 border-l-4 border-emerald-300">
                    <p className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Compte-rendu</p>
                    <p className="text-slate-700 italic line-clamp-3">« {mission.completion_note} »</p>
                  </div>
                )}

                {mission.status === 'terminee' && mission.completion_images?.length > 0 && (
                  <div>
                    <p className="text-slate-500 mb-1.5">Photos ({mission.completion_images.length})</p>
                    <div className="grid grid-cols-4 gap-1">
                      {mission.completion_images.slice(0, 4).map((src, i) => (
                        <button
                          key={i}
                          onClick={() => setLightbox(src)}
                          className="aspect-square rounded border border-slate-200 overflow-hidden hover:border-[#FF5A6E] transition-colors"
                        >
                          <img src={src} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CTA Assigner (si pas de mission) */}
          {!mission && comment.issue_severity !== 'aucun_probleme' && (
            <div className="bg-gradient-to-br from-[#FFF0F1] to-white rounded-2xl border border-[#FFD9DE] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Info size={14} className="text-[#C73E51]" />
                <h3 className="text-sm font-semibold text-[#C73E51]">Action recommandée</h3>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Ce problème nécessite l'intervention d'un artisan. Assignez-le pour notifier votre prestataire.
              </p>
              <Button
                onClick={openAssignModal}
                className="w-full bg-[#FF5A6E] hover:bg-[#E84B5F] text-white"
                size="sm"
              >
                <Send size={13} className="mr-2" />
                Assigner à un artisan
              </Button>
            </div>
          )}
        </aside>
      </div>

      {/* ===== Lightbox ===== */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-8 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-slate-300 p-2"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          >
            <X size={24} />
          </button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}

      {/* ===== Assign mission modal ===== */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Assigner la mission</h3>
              <button onClick={() => setShowAssign(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Sélectionnez les prestataires ({selectedContractors.length} sélectionné{selectedContractors.length > 1 ? 's' : ''})</p>
                {contractors.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucun contact. Ajoutez des artisans dans le carnet de contacts.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {contractors.map(c => (
                      <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedContractors.includes(c.id) ? 'border-[#FF5A6E] bg-[#FFF0F1]' : 'border-slate-200 hover:bg-slate-50'
                      }`}>
                        <input
                          type="checkbox"
                          checked={selectedContractors.includes(c.id)}
                          onChange={() => toggleContractor(c.id)}
                          className="accent-[#FF5A6E]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{c.company_name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><Mail size={10} /> {c.email}</p>
                          {c.categories?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {c.categories.map(cat => (
                                <span key={cat} className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600">{cat}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Délai d'intervention</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { value: '24h', label: 'Sous 24h' },
                    { value: '48h', label: 'Sous 48h' },
                    { value: '72h', label: 'Sous 72h' },
                    { value: '7j', label: 'Sous 7 jours' },
                    { value: 'custom', label: 'Date précise' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDeadlinePreset(opt.value)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        deadlinePreset === opt.value
                          ? 'border-[#FF5A6E] bg-[#FFF0F1] text-[#FF3B55]'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {deadlinePreset === 'custom' && (
                  <input
                    type="datetime-local"
                    value={customDeadline}
                    onChange={e => setCustomDeadline(e.target.value)}
                    min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                    className="mt-2 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Note pour l'artisan</p>
                <Textarea
                  value={missionNote}
                  onChange={e => setMissionNote(e.target.value)}
                  placeholder="Ex: Clé sous le paillasson, interphone code 1234, merci de m'appeler avant..."
                  rows={3}
                  className="text-sm"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleAssign}
                  disabled={assigning || selectedContractors.length === 0}
                  className="flex-1 bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
                >
                  <Send size={14} className="mr-2" /> {assigning ? 'Envoi...' : `Envoyer la mission (${selectedContractors.length})`}
                </Button>
                <Button variant="outline" onClick={() => setShowAssign(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default IssueDetailPage;
